import React, { useEffect, useRef, useState } from "react";
import { Button, Input, Tag, Tooltip, Upload, message } from "antd";
import { getAppSettings } from "../../utils/API";
import {
  PaperClipOutlined,
  ExportOutlined,
  SendOutlined,
  DeleteOutlined,
  FileOutlined,
  FilePdfTwoTone,
  FileExcelTwoTone,
  FileWordTwoTone,
  FilePptTwoTone,
  FileImageTwoTone,
  FileZipTwoTone,
  RollbackOutlined,
  CodeOutlined,
  FileTextOutlined,
  PictureOutlined,
} from "@ant-design/icons";
import {
  downloadEmailAttachment,
  replyToEmail,
  REPLY_ATTACHMENT_MAX_BYTES,
  type EmailAttachmentMeta,
  type EmailMessageRecord,
} from "../../utils/emailLinkAPI";

// Pieces shared by both Email tabs. Nothing here knows whether it is rendering
// a customer quote or a dealer offer — that is what lets Part 2 reuse Part 1
// rather than duplicating it.

// ─── file-type icon ──────────────────────────────────────────────────────────

// Picked by extension rather than MIME type — attachments picked locally
// (before upload) and ones downloaded from Gmail both reliably carry a
// filename, but not always a trustworthy content type.
const fileIconFor = (name: string) => {
  const extension = (name || "").split(".").pop()?.toLowerCase() || "";
  if (extension === "pdf") return <FilePdfTwoTone twoToneColor="#f5222d" />;
  if (["xls", "xlsx", "xlsm", "csv"].includes(extension))
    return <FileExcelTwoTone twoToneColor="#52c41a" />;
  if (["doc", "docx"].includes(extension))
    return <FileWordTwoTone twoToneColor="#1677ff" />;
  if (["ppt", "pptx"].includes(extension))
    return <FilePptTwoTone twoToneColor="#fa8c16" />;
  if (["jpg", "jpeg", "png", "gif", "bmp", "webp", "svg"].includes(extension))
    return <FileImageTwoTone twoToneColor="#13c2c2" />;
  if (["zip", "rar", "7z"].includes(extension))
    return <FileZipTwoTone twoToneColor="#faad14" />;
  return <FileOutlined />;
};

// ─── primary button fill ─────────────────────────────────────────────────────

// Tailwind's preflight resets `button { background-color: initial }` using an
// ELEMENT selector (specificity 0,0,1), while antd v5 wraps its own styles in
// `:where(...)`, which has ZERO specificity. Preflight therefore wins, and every
// antd `type="primary"` button renders with no fill — white label on a
// transparent background, i.e. invisible.
//
// Existing pages work around this with page-scoped rules such as
// `.inventory-page .ant-btn.ant-btn-primary` in App.css. There is also a global
// rule for small buttons keyed to `.css-dev-only-do-not-override-*`, but that
// antd class only exists in development builds, so it does nothing in
// production.
//
// A class selector (0,1,0) outranks the element selector, so stating the brand
// colours here restores the fill in both dev and production without touching
// global CSS or affecting any existing page.
export const PRIMARY_BUTTON_CLASS = [
  "!bg-[#004aad] !border-[#004aad] !text-white",
  "hover:!bg-[#003a87] hover:!border-[#003a87] hover:!text-white",
  "focus:!bg-[#003a87] focus:!border-[#003a87]",
  "disabled:!bg-gray-200 disabled:!border-gray-200 disabled:!text-gray-400",
].join(" ");

// Same problem, same fix for a destructive confirm: the global small-button
// rule explicitly skips `.ant-btn-dangerous`, so a danger button renders with
// no fill either.
export const DANGER_BUTTON_CLASS = [
  "!bg-[#d92d20] !border-[#d92d20] !text-white",
  "hover:!bg-[#b42318] hover:!border-[#b42318] hover:!text-white",
  "focus:!bg-[#b42318] focus:!border-[#b42318]",
  "disabled:!bg-gray-200 disabled:!border-gray-200 disabled:!text-gray-400",
].join(" ");

// Popconfirm renders its OK button as a small primary button, so it hits the
// same invisible-fill bug. Spread these onto every Popconfirm so the confirm
// action stays as readable as Cancel.
export const POPCONFIRM_OK_PROPS = { className: PRIMARY_BUTTON_CLASS };
export const POPCONFIRM_DANGER_OK_PROPS = {
  danger: true,
  className: DANGER_BUTTON_CLASS,
};

// ─── formatting ──────────────────────────────────────────────────────────────

export const formatDateTime = (value?: string | null): string => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export const formatBytes = (bytes?: number | null): string => {
  const size = Number(bytes || 0);
  if (!size) return "";
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${Math.round(size / 1024)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
};

export const senderLabel = (record: EmailMessageRecord): string =>
  record.fromName || record.fromEmail || "(unknown sender)";

// ─── sender avatar ───────────────────────────────────────────────────────────

// Outlook shows a round monogram beside every sender. Colour is derived from the
// sender string so the same person keeps the same swatch across the thread.
const AVATAR_COLORS = [
  "#0078d4",
  "#038387",
  "#8764b8",
  "#ca5010",
  "#498205",
  "#c239b3",
  "#005b70",
  "#986f0b",
];

export const initialsOf = (record: EmailMessageRecord): string => {
  const source = record.fromName || record.fromEmail || "?";
  const parts = source
    .replace(/[^\p{L}\p{N} ]/gu, " ")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return source.slice(0, 2).toUpperCase();
};

export const SenderAvatar: React.FC<{
  record: EmailMessageRecord;
  size?: number;
}> = ({ record, size = 32 }) => {
  const key = record.fromEmail || record.fromName || "?";
  let hash = 0;
  for (let i = 0; i < key.length; i += 1) {
    hash = (hash * 31 + key.charCodeAt(i)) | 0;
  }
  const background = AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
  return (
    <span
      className="inline-flex shrink-0 select-none items-center justify-center rounded-full font-semibold text-white"
      style={{
        width: size,
        height: size,
        background,
        fontSize: Math.round(size * 0.4),
      }}
    >
      {initialsOf(record)}
    </span>
  );
};

// ─── message list row ────────────────────────────────────────────────────────

// One line in an Outlook-style message list: avatar, direction dot, sender,
// date, subject, preview. Shared by the linked-history panel and the unmatched
// inbox so both read identically.
export const MessageListRow: React.FC<{
  record: EmailMessageRecord;
  active: boolean;
  onClick: () => void;
  /** Small muted note under the preview, e.g. "attached manually". */
  note?: React.ReactNode;
  /** Tighter row for the quote page's email tabs, which have less height. */
  compact?: boolean;
}> = ({ record, active, onClick, note, compact = false }) => {
  const inbound = record.direction === "in";
  return (
    <button
      type="button"
      onClick={onClick}
      className={`relative flex w-full text-left transition-colors ${
        compact ? "gap-2 px-2 py-1.5" : "gap-3 px-3 py-2.5"
      } ${active ? "bg-[#eaf3fb]" : "hover:bg-gray-50"}`}
    >
      {active ? (
        <span className="absolute inset-y-0 left-0 w-[3px] bg-[#0078d4]" />
      ) : null}
      <SenderAvatar record={record} size={compact ? 22 : 32} />
      <div className="min-w-0 flex-1">
        <div className={`flex items-baseline ${compact ? "gap-1.5" : "gap-2"}`}>
          <span
            className={`shrink-0 rounded-full ${
              compact ? "h-1.5 w-1.5" : "h-2 w-2"
            } ${inbound ? "bg-[#0078d4]" : "bg-emerald-500"}`}
          />
          <span
            className={`truncate font-semibold text-gray-900 ${
              compact ? "text-[11px] leading-4" : "text-[13px]"
            }`}
          >
            {senderLabel(record)}
          </span>
          <span
            className={`ml-auto shrink-0 text-gray-400 ${
              compact ? "text-[10px] leading-4" : "text-[11px]"
            }`}
          >
            {formatDateTime(record.sentAt || record.receivedAt)}
          </span>
        </div>
        <div
          className={`truncate text-gray-700 ${
            compact ? "text-[11px] leading-4" : "text-[13px]"
          }`}
        >
          {record.subject || "(no subject)"}
        </div>
        <div className="flex items-center gap-1">
          <span
            className={`truncate text-gray-400 ${
              compact ? "text-[10px] leading-4" : "text-[12px]"
            }`}
          >
            {record.bodyPreview || senderLabel(record)}
          </span>
          {record.hasAttachments ? (
            <PaperClipOutlined
              className={`ml-auto shrink-0 text-gray-400 ${
                compact ? "!text-[10px]" : ""
              }`}
            />
          ) : null}
        </div>
        {note ? (
          <div
            className={`text-gray-300 ${
              compact ? "text-[10px] leading-4" : "mt-0.5 text-[11px]"
            }`}
          >
            {note}
          </div>
        ) : null}
      </div>
    </button>
  );
};

// ─── Open in Email ─────────────────────────────────────────────────────────

// webLink is null for mail read over IMAP (there is no web link to a message),
// so this button simply does not render; it stays for any older synced rows.
export const OpenInMailButton: React.FC<{
  record: EmailMessageRecord;
  size?: "small" | "middle";
}> = ({ record, size = "small" }) => {
  if (!record.webLink) return null;
  return (
    <Tooltip title="Open this message in your email">
      <Button
        size={size}
        icon={<ExportOutlined />}
        onClick={() => window.open(record.webLink as string, "_blank", "noopener")}
      >
        Open in Email
      </Button>
    </Tooltip>
  );
};

// ─── Open Outlook ────────────────────────────────────────────────────────────

// Launches the Outlook desktop app through the `outlook:` protocol handler that
// Outlook registers on Windows.
//
// It can only open the Inbox, NOT this message: `outlook:` addresses an item by
// its EntryID inside the local Outlook data file, and a message pulled over the
// Gmail API has no EntryID. So the subject is copied to the clipboard on the way
// out — one paste into Outlook's search box lands on the message.
//
// If Outlook is not installed the browser silently does nothing and gives us no
// error event, which is why the tooltip states the requirement up front.
export const OpenOutlookButton: React.FC<{
  record: EmailMessageRecord;
  size?: "small" | "middle";
}> = ({ record: _record, size: _size = "small" }) => {
  void _record;
  void _size;

  /*
  const open = async () => {
    const subject = record.subject || "";
    if (subject) {
      try {
        await navigator.clipboard.writeText(subject);
        message.info("Subject copied — paste it into Outlook's search box");
      } catch {
        // Clipboard needs a secure context and permission; not worth blocking
        // the app launch over.
      }
    }
    // Assigning to location (rather than window.open) avoids leaving a blank
    // tab behind when the protocol hands off to the desktop app.
    window.location.href = "outlook:inbox";
  };
  */

  return null;
};

// ─── attachments ─────────────────────────────────────────────────────────────

// Metadata comes from our own table, so the list renders even when the mail
// server is down. Only the click-through download needs it live.
export const AttachmentChips: React.FC<{
  emailId: number;
  attachments: EmailAttachmentMeta[];
  /** Tighter chips, for the height-starved Sales View tabs. */
  compact?: boolean;
}> = ({ emailId, attachments, compact = false }) => {
  const [busyId, setBusyId] = useState<number | null>(null);

  // Inline images are part of the body, not real attachments the user wants.
  const visible = (attachments || []).filter((item) => !item.isInline);
  if (!visible.length) return null;

  const handleDownload = async (attachment: EmailAttachmentMeta) => {
    setBusyId(attachment.id);
    try {
      await downloadEmailAttachment(emailId, attachment);
    } catch (error: any) {
      message.error(
        error?.response?.data?.message ||
          error?.message ||
          "Could not download the attachment",
      );
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className={`flex flex-wrap ${compact ? "gap-1.5" : "gap-2"}`}>
      {visible.map((attachment) => (
        <Button
          key={attachment.id}
          size="small"
          icon={busyId === attachment.id ? undefined : fileIconFor(attachment.name || "")}
          loading={busyId === attachment.id}
          onClick={() => handleDownload(attachment)}
          className={`!h-auto !rounded-none !border-gray-300 !bg-gray-50 !text-left !text-gray-800 hover:!border-gray-400 hover:!bg-white hover:!text-gray-900 ${
            compact
              ? "!px-2 !py-0.5 !text-[11px] !leading-5"
              : "!px-2.5 !py-1.5 !text-xs"
          }`}
        >
          {attachment.name}
          {formatBytes(attachment.size) ? (
            <span className="text-gray-400"> · {formatBytes(attachment.size)}</span>
          ) : null}
        </Button>
      ))}
    </div>
  );
};

// ─── message body ────────────────────────────────────────────────────────────

// Email HTML is untrusted third-party content written by whoever sent the mail,
// so it is rendered inside a SANDBOXED IFRAME rather than injected into the
// portal's DOM. `sandbox` with no allow-* tokens gives the frame an opaque
// origin and blocks scripts, forms and navigation outright, and the frame also
// contains the sender's CSS so a dealer's stylesheet cannot restyle the portal.
// This needs no sanitiser dependency and is what webmail clients do.
const IFRAME_SANDBOX = "";

export const EmailBody: React.FC<{ record: EmailMessageRecord }> = ({ record }) => {
  const frameRef = useRef<HTMLIFrameElement | null>(null);
  const [height, setHeight] = useState(120);

  if (!record.bodyHtml) {
    return (
      <p className="whitespace-pre-wrap text-sm text-gray-700">
        {record.bodyPreview || "(no content)"}
      </p>
    );
  }

  // Grow the frame to fit its content so there is no nested scrollbar. Capped
  // because the surrounding container scrolls anyway.
  const fitToContent = () => {
    const doc = frameRef.current?.contentDocument;
    if (!doc?.body) return;
    setHeight(Math.min(2000, Math.max(120, doc.body.scrollHeight + 24)));
  };

  const srcDoc = `<!doctype html><meta charset="utf-8">
<base target="_blank">
<style>
  body{margin:0;font:14px/1.5 system-ui,-apple-system,"Segoe UI",sans-serif;color:#1f2937;word-break:break-word}
  img{max-width:100%;height:auto}
  table{max-width:100%}
  blockquote{margin:8px 0;padding-left:12px;border-left:3px solid #e5e7eb;color:#6b7280}
</style>
${record.bodyHtml}`;

  return (
    <iframe
      // Fresh element per email: re-pointing an existing iframe at new
      // srcDoc adds a browser-history entry each time, which piles up and
      // makes Back step through every email viewed.
      key={record.id}
      ref={frameRef}
      title={`Email ${record.id}`}
      sandbox={IFRAME_SANDBOX}
      srcDoc={srcDoc}
      onLoad={fitToContent}
      // `grow` lets it stretch to fill the reading pane when there is spare
      // height; `minHeight` keeps it at least as tall as its own content.
      className="w-full grow border-0"
      style={{ minHeight: height }}
    />
  );
};

const escapeHtml = (value: string): string =>
  value.replace(/[&<>"']/g, (character) => {
    switch (character) {
      case "&":
        return "&amp;";
      case "<":
        return "&lt;";
      case ">":
        return "&gt;";
      case '"':
        return "&quot;";
      default:
        return "&#39;";
    }
  });

const QuotedEmailBody: React.FC<{
  record: EmailMessageRecord;
  originalFrom: string;
  originalTo: string;
  originalWhen: string;
  /**
   * In the quote page's email tabs the whole compose pane is only a few
   * hundred pixels tall, so the quoted original starts collapsed behind a
   * toggle instead of claiming a 180px minimum the reply box then loses.
   */
  compact?: boolean;
}> = ({ record, originalFrom, originalTo, originalWhen, compact = false }) => {
  const [open, setOpen] = useState(true);
  const frameRef = useRef<HTMLIFrameElement | null>(null);
  const [frameHeight, setFrameHeight] = useState(180);
  // Size the frame to its content so the whole compose pane (reply text +
  // quoted original) scrolls as one, like Outlook.
  const fitToContent = () => {
    const doc = frameRef.current?.contentDocument;
    if (doc?.body) setFrameHeight(Math.max(120, doc.body.scrollHeight + 8));
  };
  const bodyHtml = record.bodyHtml
    ? record.bodyHtml
    : `<p class="plain">${escapeHtml(record.bodyPreview || "(no content)")}</p>`;
  const srcDoc = `<!doctype html><meta charset="utf-8">
<base target="_blank">
<style>
  body{margin:0;padding:0 0 12px 0;font:14px/1.5 system-ui,-apple-system,"Segoe UI",sans-serif;color:#111827;word-break:break-word}
  img{max-width:100%;height:auto}
  table{max-width:100%}
  blockquote{margin:8px 0;padding-left:12px;border-left:3px solid #e5e7eb;color:#6b7280}
  .quoted-headers{margin:0 0 24px 0;font-size:12px;line-height:1.6;color:#111827}
  .quoted-headers div{margin:0}
  .plain{white-space:pre-wrap}
</style>
<div class="quoted-headers">
  <div><strong>From:</strong> ${escapeHtml(originalFrom)}</div>
  <div><strong>Sent:</strong> ${escapeHtml(originalWhen)}</div>
  <div><strong>To:</strong> ${escapeHtml(originalTo)}</div>
  <div><strong>Subject:</strong> ${escapeHtml(record.subject || "(no subject)")}</div>
</div>
${bodyHtml}`;

  if (compact && !open) {
    return (
      <div className="mt-2 shrink-0 border-t border-gray-200 pt-1.5">
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="text-[11px] text-gray-500 underline hover:text-gray-700"
        >
          Show original message
        </button>
      </div>
    );
  }

  return (
    <div className="mt-1 flex shrink-0 flex-col border-t border-gray-200 pt-0">
      <iframe
        key={record.id}
        title={`Quoted email ${record.id}`}
        // Same-origin (no scripts) only so the height can be measured.
        sandbox="allow-same-origin"
        ref={frameRef}
        srcDoc={srcDoc}
        onLoad={fitToContent}
        style={{ height: frameHeight }}
        className="w-full shrink-0 border-0"
      />
    </div>
  );
};

// Typing a space after an address closes it with ", " so the next one starts
// clean. Spaces right after a comma (or at the very start) are left alone.
const commaOnSpace = (value: string) => {
  if (!/\s$/.test(value)) return value;
  const trimmed = value.replace(/\s+$/, "");
  if (!trimmed) return "";
  return trimmed.endsWith(",") ? `${trimmed} ` : `${trimmed}, `;
};

// ─── reply ───────────────────────────────────────────────────────────────────

// Reply / reply-all go out over SMTP from the connected mailbox. The backend
// writes the In-Reply-To / References headers so the reply stays in the same
// conversation, and a copy is saved to the Sent folder.
//
// Styled as a compact compose pane — To / Cc / Subject rows above the body,
// like a real mail client's reply window — rather than a bare textarea. It
// only exists while the user is actively replying: MessageCard mounts this in
// place of the message header/body, and unmounts it back to the reply icon
// when they cancel or the send succeeds.
export const ReplyComposePanel: React.FC<{
  record: EmailMessageRecord;
  /** Which icon the caller opened this panel from — fixes the send action. */
  replyAll: boolean;
  onSent?: () => void;
  onCancel: () => void;
  /** Tighter compose chrome for the quote page's short email tabs. */
  compact?: boolean;
}> = ({ record, replyAll, onSent, onCancel, compact = false }) => {
  const [body, setBody] = useState("");
  // antd's TextArea ref (proxies focus/setSelectionRange to the underlying
  // <textarea>), typed loosely to avoid pulling in rc-textarea's types here.
  const bodyTextAreaRef = useRef<any>(null);
  const attachListRef = useRef<HTMLDivElement | null>(null);
  const [cc, setCc] = useState("");
  const [subject, setSubject] = useState(() =>
    /^re:/i.test(record.subject || "")
      ? record.subject || ""
      : `RE: ${record.subject || "(no subject)"}`,
  );
  const [files, setFiles] = useState<File[]>([]);
  const [sending, setSending] = useState(false);

  const inbound = record.direction === "in";
  // Who the reply goes to: the sender (or original recipients) on the plain
  // Reply, plus everyone else who was on the message for Reply all. This is a
  // preview only — the backend works out the exact To/Cc from the original
  // headers, so it does not need to match byte for byte.
  const primary = inbound
    ? record.fromEmail || senderLabel(record)
    : (record.toEmails || []).join(", ") || "-";
  const toLine = replyAll
    ? Array.from(new Set([primary, ...(record.toEmails || [])])).join(", ")
    : primary;
  const [to, setTo] = useState(toLine);
  const originalFrom =
    record.fromName && record.fromEmail && record.fromName !== record.fromEmail
      ? `${record.fromName} <${record.fromEmail}>`
      : record.fromEmail || senderLabel(record);
  const originalTo = (record.toEmails || []).join(", ") || "-";
  const originalWhen = formatDateTime(record.sentAt || record.receivedAt);

  useEffect(() => {
    const isFiles = (event: DragEvent) =>
      Array.from(event.dataTransfer?.types || []).includes("Files");
    const onEnter = (event: DragEvent) => {
      if (!isFiles(event)) return;
      dragDepth.current += 1;
      // Quote-page tab (compact): cover list + message. Full Emails page:
      // just this reply pane.
      const zone =
        (compact && rootRef.current?.closest("[data-email-drop-zone]")) ||
        rootRef.current?.parentElement;
      const rect = zone?.getBoundingClientRect();
      if (rect) {
        setOverlayRect({
          top: rect.top,
          left: rect.left,
          width: rect.width,
          height: rect.height,
        });
      }
      setDragging(true);
    };
    const onLeave = (event: DragEvent) => {
      if (!isFiles(event)) return;
      dragDepth.current = Math.max(0, dragDepth.current - 1);
      if (dragDepth.current === 0) setDragging(false);
    };
    const reset = () => {
      dragDepth.current = 0;
      setDragging(false);
    };
    window.addEventListener("dragenter", onEnter);
    window.addEventListener("dragleave", onLeave);
    window.addEventListener("drop", reset);
    window.addEventListener("dragend", reset);
    return () => {
      window.removeEventListener("dragenter", onEnter);
      window.removeEventListener("dragleave", onLeave);
      window.removeEventListener("drop", reset);
      window.removeEventListener("dragend", reset);
    };
  }, []);

  // Focus the body on open without scrolling the pane — in the short quote-page
  // tab a scrolling focus pushes Send / To / Cc out of view.
  useEffect(() => {
    bodyTextAreaRef.current?.resizableTextArea?.textArea?.focus({
      preventScroll: true,
    });
  }, []);

  // Pre-fill the reply body with the signature configured on the Email
  // Masters page (top "Reply Signature" box), so it's there by default every
  // time a reply is opened — cursor is placed above it so typing doesn't
  // disturb it.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const settings = await getAppSettings();
        const signature = String(settings?.emailSignature || "").trim();
        if (cancelled || !signature) return;
        setBody(`\n\n${signature}`);
        requestAnimationFrame(() => {
          // antd's TextArea ref does not expose setSelectionRange/focus
          // itself — only the wrapped native <textarea>, at this path.
          const nativeTextArea: HTMLTextAreaElement | undefined =
            bodyTextAreaRef.current?.resizableTextArea?.textArea;
          nativeTextArea?.focus({ preventScroll: true });
          nativeTextArea?.setSelectionRange(0, 0);
        });
      } catch {
        // No signature configured, or settings unreachable — reply just
        // starts blank, same as before this feature existed.
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const totalBytes = files.reduce((sum, file) => sum + file.size, 0);
  const overLimit = totalBytes > REPLY_ATTACHMENT_MAX_BYTES;

  // Picked files are held here and posted with the reply, so nothing is
  // uploaded until Reply is actually pressed.
  // Drag-and-drop: a file dragged anywhere over the page raises the overlay on
  // this pane (window-level, because the quoted-mail iframe swallows drag
  // events of its own). The counter keeps it steady across child elements.
  const [dragging, setDragging] = useState(false);
  const [overlayRect, setOverlayRect] = useState<{
    top: number;
    left: number;
    width: number;
    height: number;
  } | null>(null);
  const dragDepth = useRef(0);
  const rootRef = useRef<HTMLDivElement | null>(null);

  const addFiles = (picked: File[]) => {
    setFiles((current) => {
      const seen = new Set(current.map((file) => `${file.name}:${file.size}`));
      return [
        ...current,
        ...picked.filter((file) => !seen.has(`${file.name}:${file.size}`)),
      ];
    });
  };

  const removeFile = (index: number) =>
    setFiles((current) => current.filter((_, position) => position !== index));

  // A newly added file lands at the bottom of the list — keep it in view.
  React.useEffect(() => {
    const el = attachListRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [files.length]);

  const send = async () => {
    const text = body.trim();
    if (!text) {
      message.warning("Write a reply first");
      return;
    }

    if (overLimit) {
      message.warning("Attachments total more than 15 MB — remove one first");
      return;
    }

    if (!to.trim()) {
      message.warning("Add at least one recipient in To");
      return;
    }

    setSending(true);
    try {
      // Plain text in, simple HTML out. Swap this for the Tiptap editor already
      // used on the Email Masters page if rich formatting is ever needed.
      const html = text
        .split(/\n{2,}/)
        .map((para) => `<p>${para.replace(/\n/g, "<br/>")}</p>`)
        .join("");

      const result = await replyToEmail(record.id, {
        body: html,
        replyAll,
        // Only sent when the user changed it; otherwise the backend works out
        // the recipients from the original headers as before.
        to: to.trim() !== toLine.trim() ? to.trim() : undefined,
        cc: cc.trim() || undefined,
        subject: subject.trim() || undefined,
        attachments: files,
      });
      message.success(result.message || "Reply sent");
      setBody("");
      setCc("");
      setTo(toLine);
      setSubject(
        /^re:/i.test(record.subject || "")
          ? record.subject || ""
          : `RE: ${record.subject || "(no subject)"}`,
      );
      setFiles([]);
      onSent?.();
      onCancel();
    } catch (error: any) {
      message.error(
        error?.response?.data?.message || error?.message || "Could not send the reply",
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      ref={rootRef}
      className={`relative flex flex-col bg-white ${
        // Compact (short quote-page tab): let the panel grow to its content
        // so the wrapper scrolls Send/To/Cc/body/original as one.
        compact ? "" : "h-full min-h-0 flex-1 overflow-hidden"
      }`}
    >
      {dragging ? (
        // Fixed to the email area's on-screen rect, so it covers the list too
        // and stays put however the (compact) panel is scrolled.
        <div
          style={{ ...(overlayRect || { top: 0, left: 0, width: 0, height: 0 }) }}
          className="fixed z-50 flex flex-col items-center justify-center bg-white/95"
            onDragOver={(event) => event.preventDefault()}
            onDrop={(event) => {
              event.preventDefault();
              dragDepth.current = 0;
              setDragging(false);
              const dropped = Array.from(event.dataTransfer.files || []);
              if (dropped.length && !sending) addFiles(dropped);
            }}
          >
            {/* Stacked file cards, like ChatGPT's drop target. */}
            <div className="relative mb-2 h-14 w-20">
              <div className="absolute left-0 top-2 flex h-9 w-11 -rotate-12 items-center justify-center rounded-lg bg-indigo-400 text-white shadow">
                <CodeOutlined />
              </div>
              <div className="absolute right-0 top-0 flex h-10 w-9 rotate-12 items-center justify-center rounded-lg bg-violet-400 text-white shadow">
                <FileTextOutlined />
              </div>
              <div className="absolute bottom-0 left-6 flex h-10 w-10 items-center justify-center rounded-xl bg-blue-600 text-lg text-white shadow-md">
                <PictureOutlined />
              </div>
            </div>
            <div className="text-lg font-semibold text-gray-900">Add anything</div>
            <div className="text-xs text-gray-600">
              Drop any file here to add it to the reply
            </div>
        </div>
      ) : null}

      {/* Discard — leaves the reply without sending. */}
      <div
        className={`flex shrink-0 items-center justify-end gap-2 px-3 ${
          compact ? "py-0" : "py-1.5"
        }`}
      >
        {/* <span className="mr-auto text-xs text-gray-400">
          Saved to Sent Mail when sent
        </span> */}
        <Button
          type="text"
          size="small"
          className={compact ? "!h-6 !px-1.5 !text-[11px]" : undefined}
          icon={<DeleteOutlined />}
          onClick={onCancel}
          disabled={sending}
        >
          Discard
        </Button>
      </div>

      {/* Outlook's compose header: Send on the left, spanning the To and Cc
          rows, with Subject beneath them. */}
      <div
        className={`flex shrink-0 items-start border-b border-gray-100 px-3 ${
          compact ? "gap-2 pb-1" : "gap-3 pb-2"
        }`}
      >
        <Tooltip title={replyAll ? "Reply all" : "Reply"}>
          <Button
            className={`!flex !flex-col !items-center !justify-center !gap-0.5 !rounded-none !border-gray-300 !text-gray-700 hover:!border-gray-400 ${
              compact ? "!h-[46px] !w-[46px]" : "!h-[68px] !w-[68px]"
            }`}
            loading={sending}
            disabled={sending || overLimit}
            onClick={send}
          >
            {sending ? null : (
              <SendOutlined className={compact ? "!text-sm" : "!text-lg"} />
            )}
            <span className={compact ? "text-[10px] leading-3" : "text-xs"}>
              Send
            </span>
          </Button>
        </Tooltip>

        <div className={`min-w-0 flex-1 ${compact ? "text-[11px]" : "text-sm"}`}>
          <div className={`flex items-center ${compact ? "gap-2" : "gap-3"}`}>
            <span
              className={`flex shrink-0 items-center justify-center border border-gray-300 text-gray-600 ${
                compact ? "h-5 w-14 text-[10px]" : "h-7 w-20 text-xs"
              }`}
            >
              To
            </span>
            <Input
              value={to}
              onChange={(event) => setTo(commaOnSpace(event.target.value))}
              placeholder="Comma separated"
              bordered={false}
              size={compact ? "small" : "middle"}
              className={`min-w-0 flex-1 !rounded-none !border-b !border-gray-200 !px-0 !text-gray-800 ${
                compact ? "!h-5 !py-0 !text-[11px]" : ""
              }`}
            />
          </div>
          <div
            className={`flex items-center ${
              compact ? "gap-2" : "mt-1 gap-3"
            }`}
          >
            <span
              className={`flex shrink-0 items-center justify-center border border-gray-300 text-gray-600 ${
                compact ? "h-5 w-14 text-[10px]" : "h-7 w-20 text-xs"
              }`}
            >
              Cc
            </span>
            <Input
              value={cc}
              onChange={(event) => setCc(commaOnSpace(event.target.value))}
              placeholder="Optional, comma separated"
              bordered={false}
              size={compact ? "small" : "middle"}
              className={`min-w-0 flex-1 !rounded-none !border-b !border-gray-200 !px-0 ${
                compact ? "!h-5 !py-0 !text-[11px]" : ""
              }`}
            />
          </div>
          <div
            className={`flex items-center ${
              compact ? "gap-2" : "mt-1 gap-3"
            }`}
          >
            <span
              className={`shrink-0 whitespace-nowrap text-right text-gray-400 ${
                compact ? "w-14 text-[10px]" : "w-20 text-xs"
              }`}
            >
              Subject
            </span>
            <Input
              value={subject}
              onChange={(event) => setSubject(event.target.value)}
              bordered={false}
              size={compact ? "small" : "middle"}
              className={`min-w-0 flex-1 !rounded-none !border-b !border-gray-200 !px-0 !text-gray-800 ${
                compact ? "!h-5 !py-0 !text-[11px]" : ""
              }`}
            />
          </div>
          <div
            className={`flex items-start ${
              compact ? "gap-2" : "mt-1 gap-3"
            }`}
          >
            <span
              className={`shrink-0 whitespace-nowrap text-right text-gray-400 ${
                compact ? "w-14 text-[10px] leading-5" : "w-20 text-xs leading-8"
              }`}
            >
              Attachment
            </span>

            {/* Shows 3 attachments; the 4th onwards scroll inside this box. */}
            <div
              ref={attachListRef}
              className={`thin-scroll flex min-w-0 flex-1 flex-col gap-1.5 overflow-y-auto ${
                compact ? "max-h-[72px]" : "max-h-[108px]"
              }`}
            >
              {files.map((file, index) => (
                <Tag
                  key={`${file.name}-${index}`}
                  className={`!m-0 flex w-fit items-center gap-1 ${
                    compact ? "!py-0 !text-[10px]" : "!py-1"
                  }`}
                  icon={fileIconFor(file.name)}
                >
                  <span
                    className={`truncate ${compact ? "max-w-[9rem]" : "max-w-[16rem]"}`}
                  >
                    {file.name}
                  </span>
                  <Button
                    type="text"
                    size="small"
                    className="!h-4 !w-4 !min-w-0 !p-0"
                    icon={<DeleteOutlined className="text-xs" />}
                    disabled={sending}
                    onClick={() => removeFile(index)}
                  />
                </Tag>
              ))}
              {overLimit ? (
                <span
                  className={`font-medium text-red-500 ${compact ? "text-[10px]" : "text-xs"}`}
                >
                  {formatBytes(totalBytes)} of 15 MB — remove one to send
                </span>
              ) : null}
            </div>

            <Upload
              multiple
              showUploadList={false}
              // Returning false keeps antd from posting anything itself — the
              // file rides along with the reply instead.
              beforeUpload={(file) => {
                addFiles([file as unknown as File]);
                return false;
              }}
            >
              <Button
                size="small"
                className={`!min-w-0 !shrink-0 !p-0 ${compact ? "!h-5 !w-5" : "!h-6 !w-6"}`}
                icon={<PaperClipOutlined />}
                disabled={sending}
              />
            </Upload>
          </div>
        </div>
      </div>

      <div
        className={`flex flex-col ${
          compact ? "shrink-0 px-2 pb-1 pt-0.5" : "min-h-0 flex-1 overflow-y-auto p-3"
        }`}
      >
        <Input.TextArea
          ref={bodyTextAreaRef}
          value={body}
          onChange={(event) => setBody(event.target.value)}
          // Outlook-style: no box, just a caret at the top of the message.
          placeholder=""
          bordered={false}
          autoSize={
            compact ? { minRows: 4, maxRows: 8 } : { minRows: 2, maxRows: 8 }
          }
          className={`reply-body-input shrink-0 ${
            compact ? "!text-[11px]" : ""
          }`}
        />
  
        <QuotedEmailBody
          record={record}
          originalFrom={originalFrom}
          originalTo={originalTo}
          originalWhen={originalWhen}
          compact={compact}
        />
      </div>
    </div>
  );
};

// ─── one message, expanded ───────────────────────────────────────────────────

export const MessageCard: React.FC<{
  record: EmailMessageRecord;
  showReply?: boolean;
  onReplySent?: () => void;
  extraActions?: React.ReactNode;
  /**
   * Hide the Open in Email / Open Outlook row. Set when the host page already
   * renders those actions somewhere else, e.g. the Unmatched tab bar.
   */
  showCommandBar?: boolean;
  /** Stretch to the parent's height and scroll the body, not the whole card. */
  fill?: boolean;
  /**
   * Shrink the header chrome (avatar, subject, Reply buttons, timestamp, tag)
   * so the message body gets the space. Set by the quote page's Cust Email /
   * Dealer Emails tabs, which have far less height than the Emails page.
   */
  compact?: boolean;
}> = ({
  record,
  showReply = true,
  onReplySent,
  extraActions,
  showCommandBar = true,
  fill = false,
  compact = false,
}) => {
  // null = showing the message; "reply" / "replyAll" = which icon opened the
  // compose panel, which also fixes the panel's single send action.
  const [replyMode, setReplyMode] = useState<"reply" | "replyAll" | null>(null);
  const inbound = record.direction === "in";
  const when = formatDateTime(record.sentAt || record.receivedAt);
  // Only the other side of the conversation is worth a line here: on a received
  // mail that is who it came from, on a sent one who it went to. Our own
  // mailbox is on the opposite end of every message, so showing it says nothing.
  const counterparty = inbound
    ? record.fromEmail || senderLabel(record)
    : (record.toEmails || []).join(", ") || "-";

  // Switching to a different message always closes an in-progress reply — it
  // belongs to the message it was opened from, not to whatever is selected now.
  React.useEffect(() => {
    setReplyMode(null);
  }, [record.id]);

  if (showReply && replyMode) {
    return (
      <div
        className={`flex flex-col rounded-lg border border-gray-200 bg-white ${
          // In the short quote-page tabs the compose pane can outgrow its
          // container, so it scrolls rather than clipping Send / Discard.
          compact ? "max-h-full overflow-auto" : "overflow-hidden"
        } ${fill ? "lg:h-full" : ""}`}
      >
        <ReplyComposePanel
          record={record}
          compact={compact}
          replyAll={replyMode === "replyAll"}
          onSent={onReplySent}
          onCancel={() => setReplyMode(null)}
        />
      </div>
    );
  }

  return (
    <div
      className={`flex flex-col overflow-hidden rounded-lg border border-gray-200 bg-white ${
        fill ? "lg:h-full" : ""
      }`}
    >
      {/* Compact header: subject line, then one sender line. In `compact` it is
          tightened further — every pixel here is one the body doesn't get. */}
      <div
        className={`flex items-start ${
          compact ? "gap-2 px-3 pb-1 pt-1.5" : "gap-2.5 px-4 pb-1.5 pt-2"
        }`}
      >
        <SenderAvatar record={record} size={compact ? 20 : 26} />
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-2">
            <h2
              className={`m-0 min-w-0 flex-1 truncate font-semibold text-gray-900 ${
                compact ? "text-[13px] leading-5" : "text-[15px]"
              }`}
            >
              {record.subject || "(no subject)"}
            </h2>
            {/* Reply / Reply all toggles sit directly above the timestamp —
                click either to replace this header/body with the reply
                compose panel, already set to that action. */}
            <div
              className={`flex shrink-0 flex-col items-end ${
                compact ? "" : "gap-0.5"
              }`}
            >
              {showReply ? (
                <div className="flex items-center gap-1">
                  <Button
                    size="small"
                    className={`!rounded-none !border-gray-300 !bg-white !text-gray-800 hover:!border-gray-400 hover:!text-gray-900 ${
                      compact ? "!h-6 !px-1.5 !text-[11px]" : "!h-8 !px-2 !text-xs"
                    }`}
                    icon={
                      <RollbackOutlined
                        className={`!text-[#b146c2] ${compact ? "!text-[11px]" : ""}`}
                      />
                    }
                    onClick={() => setReplyMode("reply")}
                  >
                    Reply
                  </Button>
                  <Button
                    size="small"
                    className={`!rounded-none !border-gray-300 !bg-white !text-gray-800 hover:!border-gray-400 hover:!text-gray-900 ${
                      compact ? "!h-6 !px-1.5 !text-[11px]" : "!h-8 !px-2 !text-xs"
                    }`}
                    icon={
                      <RollbackOutlined
                        className={`!text-[#b146c2] ${compact ? "!text-[11px]" : ""}`}
                      />
                    }
                    onClick={() => setReplyMode("replyAll")}
                  >
                    Reply All
                  </Button>
                </div>
              ) : null}
              <span
                className={`whitespace-nowrap text-gray-400 ${
                  compact ? "text-[10px] leading-4" : "text-xs"
                }`}
              >
                {when}
              </span>
            </div>
          </div>
          <div
            className={`flex flex-wrap items-center text-gray-500 ${
              compact ? "gap-x-1.5 text-[11px]" : "mt-0.5 gap-x-2 text-xs"
            }`}
          >
            <Tag
              color={inbound ? "blue" : "green"}
              className={`!m-0 ${
                compact ? "!px-1 !text-[10px] !leading-4" : "!px-1.5 !leading-5"
              }`}
            >
              {inbound ? "Received" : "Sent"}
            </Tag>
            <span className="font-medium text-gray-700">{counterparty}</span>
            {record.hasAttachments ? (
              <PaperClipOutlined className="text-gray-400" />
            ) : null}
          </div>
        </div>
      </div>

      {/* Command bar — the row of actions above the message. */}
      {showCommandBar ? (
        <div
          className={`flex flex-wrap items-center gap-1.5 border-y border-gray-100 bg-gray-50 px-3 ${
            compact ? "py-0.5" : "py-1"
          }`}
        >
          <OpenInMailButton record={record} />
          {extraActions}
        </div>
      ) : null}

      {/* Body + attachments — the flexible part. Reply is no longer pinned
          below it; it takes over the whole card when the reply icon above is
          clicked (see the early return above). */}
      <div
        className={`flex flex-col overflow-auto pb-2 ${
          compact ? "px-3 pt-1.5" : "px-4 pt-2"
        } ${
          fill ? "max-h-[60vh] lg:max-h-none lg:min-h-0 lg:flex-1" : "max-h-[60vh]"
        }`}
      >
        <div className={`shrink-0 ${compact ? "pb-1.5" : "pb-2"}`}>
          <AttachmentChips
            emailId={record.id}
            attachments={record.attachments}
            compact={compact}
          />
        </div>
        <EmailBody record={record} />
      </div>
    </div>
  );
};
